import { ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { OffboardingArchive, OffboardingCase, OffboardingImportAudit, OffboardingPermissions,
  OffboardingQueue } from '../../models/offboarding.model';
import { OffboardingService } from '../../services/offboarding.service';
import { UserService } from '../../services/user.service';
import { NAVBAR_ROLES } from '../../navbar/navbar.constants';
import { ASSET } from '../common/employee-assets/asset.constants';
import { assetErrorKey, workflowTrackBy } from '../common/employee-assets/asset.helpers';
import { OffboardingArchiveDetailComponent } from './offboarding-archive-detail.component';
import { OFFBOARDING as O } from './offboarding.constants';

const BOARD = {mine: <OffboardingQueue>'NEEDS_ME', other: <OffboardingQueue>'OTHER_ACTIVE',
  closed: <OffboardingQueue>'CLOSED', closedTitle: 'OFFBOARD_BOARD_INTERNAL_HISTORY', closedEmpty: 'OFFBOARD_BOARD_INTERNAL_HISTORY_EMPTY',
  mineTitle: 'OFFBOARD_BOARD_MINE', otherTitle: 'OFFBOARD_BOARD_ACTIVE',
  mineEmpty: 'OFFBOARD_BOARD_MINE_EMPTY', otherEmpty: 'OFFBOARD_BOARD_ACTIVE_EMPTY',
  unavailable: 'OFFBOARD_BOARD_UNAVAILABLE', helpSuffix: '_HELP', missingRole: -1};

interface RegistryPage<T> {
  items: T[]; offset: number; nextOffset: number; hasMore: boolean; loading: boolean;
  loaded: boolean; errorKey: string; failedMore: boolean; request?: Subscription;
}
interface CaseColumn extends RegistryPage<OffboardingCase> {
  queue: OffboardingQueue; titleKey: string; helpKey: string; emptyKey: string;
}
function emptyPage<T>(): RegistryPage<T> {
  return {items: [], offset: ASSET.zero, nextOffset: ASSET.zero, hasMore: false,
    loading: false, loaded: false, errorKey: ASSET.empty, failedMore: false};
}
function caseColumn(queue: OffboardingQueue, titleKey: string, emptyKey: string): CaseColumn {
  return Object.assign(emptyPage<OffboardingCase>(), {queue: queue, titleKey: titleKey,
    helpKey: titleKey + BOARD.helpSuffix, emptyKey: emptyKey});
}

@Component({selector: 'app-offboarding-list', templateUrl: './offboarding-list.component.html',
  styleUrls: ['./offboarding-list.component.css']})
export class OffboardingListComponent implements OnInit, OnDestroy {
  trackBy = workflowTrackBy;
  constants = O;
  columns = [caseColumn(BOARD.mine, BOARD.mineTitle, BOARD.mineEmpty),
    caseColumn(BOARD.other, BOARD.otherTitle, BOARD.otherEmpty),
    caseColumn(BOARD.closed, BOARD.closedTitle, BOARD.closedEmpty)];
  archive = emptyPage<OffboardingArchive>();
  audits = emptyPage<OffboardingImportAudit>();
  permissions: OffboardingPermissions;
  permissionsLoading = false;
  permissionsError = ASSET.empty;
  isAdmin = false;
  showArchiveTools = false;
  showAudits = false;
  selectedArchive: OffboardingArchive;
  private destroyed = new Subject<void>();
  private disposed = false;
  private modal: NgbModal;
  private archiveDialog: NgbModalRef;
  constructor(private api: OffboardingService, private changeDetectorRef: ChangeDetectorRef, injector: Injector) {
    const user = injector.get(UserService).getLocal();
    this.isAdmin = !!user && !!user.roles && user.roles.indexOf(NAVBAR_ROLES.admin) !== BOARD.missingRole;
    this.modal = injector.get(NgbModal);
  }
  ngOnInit(): void { this.loadPermissions(); }
  ngOnDestroy(): void {
    this.disposed = true;
    const dialog = this.archiveDialog;
    this.archiveDialog = null; this.selectedArchive = null;
    this.destroyed.next(); this.destroyed.complete();
    if (dialog) { dialog.close(); }
  }
  loadPermissions(): void {
    if (this.permissionsLoading || this.disposed) { return; }
    this.permissionsLoading = true; this.permissionsError = ASSET.empty;
    this.api.permissions().takeUntil(this.destroyed).subscribe(result => {
      this.permissionsLoading = false;
      if (result.canViewOffboarding !== true) { this.permissionsError = ASSET.deniedKey; }
      else {
        this.permissions = result;
        this.columns.forEach(column => this.loadCases(column, false));
        if (this.isAdmin) { this.loadArchive(false); }
      }
      this.changeDetectorRef.markForCheck();
    }, error => {
      this.permissionsLoading = false; this.permissionsError = assetErrorKey(error, false);
      this.changeDetectorRef.markForCheck();
    });
  }
  loadCases(column: CaseColumn, more: boolean): void {
    if (!this.permissions || this.disposed || more && column.loading) { return; }
    this.begin(column, more);
    column.request = this.api.search(column.offset, column.queue).takeUntil(this.destroyed).subscribe(result => {
      if (result.queue !== column.queue) {
        column.loading = false; column.items = []; column.hasMore = false;
        column.failedMore = false; column.nextOffset = ASSET.zero;
        column.errorKey = BOARD.unavailable; this.changeDetectorRef.markForCheck(); return;
      }
      this.complete(column, result, more);
    }, error => this.fail(column, error));
  }
  loadArchive(more: boolean): void {
    if (!this.permissions || !this.isAdmin || this.disposed || more && this.archive.loading) { return; }
    this.begin(this.archive, more);
    this.archive.request = this.api.archive(this.archive.offset).takeUntil(this.destroyed).subscribe(
      result => this.complete(this.archive, result, more), error => this.fail(this.archive, error));
  }
  selectArchive(item: OffboardingArchive): void {
    if (!this.isAdmin || this.disposed || this.archive.items.indexOf(item) === BOARD.missingRole) { return; }
    this.selectedArchive = item;
    if (this.archiveDialog) { this.archiveDialog.componentInstance.item = item; return; }
    const dialog = this.modal.open(OffboardingArchiveDetailComponent, {windowClass: O.archiveDrawerClass});
    this.archiveDialog = dialog; dialog.componentInstance.item = item;
    dialog.result.then(() => this.clearArchiveSelection(dialog), () => this.clearArchiveSelection(dialog));
  }
  importFinished(): void {
    this.loadArchive(false);
    if (this.showAudits) { this.loadAudits(false); }
  }
  toggleArchiveTools(): void { this.showArchiveTools = !this.showArchiveTools; }
  toggleAudits(): void {
    this.showAudits = !this.showAudits;
    if (this.showAudits) { this.loadAudits(false); }
  }
  loadAudits(more: boolean): void {
    if (!this.permissions || !this.isAdmin || !this.permissions.canConfigure || this.disposed || more && this.audits.loading) { return; }
    this.begin(this.audits, more);
    this.audits.request = this.api.importAudits(this.audits.offset).takeUntil(this.destroyed).subscribe(
      result => this.complete(this.audits, result, more), error => this.fail(this.audits, error));
  }
  private begin<T>(page: RegistryPage<T>, more: boolean): void {
    if (page.request) { page.request.unsubscribe(); }
    page.offset = more ? page.nextOffset : ASSET.zero;
    page.failedMore = more; page.loading = true; page.errorKey = ASSET.empty;
  }
  private complete<T>(page: RegistryPage<T>, result: {items: T[]; hasMore: boolean; nextOffset?: number}, more: boolean): void {
    page.items = more ? page.items.concat(result.items) : result.items;
    page.nextOffset = result.nextOffset == null ? page.offset + O.limitValue : result.nextOffset;
    page.hasMore = result.hasMore; page.loading = false; page.loaded = true;
    this.changeDetectorRef.markForCheck();
  }
  private fail<T>(page: RegistryPage<T>, error: {status?: number}): void {
    page.loading = false; page.errorKey = assetErrorKey(error, false);
    if (page.errorKey === ASSET.deniedKey) { page.items = []; page.hasMore = false; }
    this.changeDetectorRef.markForCheck();
  }
  private clearArchiveSelection(dialog: NgbModalRef): void {
    if (this.disposed || this.archiveDialog !== dialog) { return; }
    this.selectedArchive = null; this.archiveDialog = null;
    this.changeDetectorRef.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-list.component.ts